import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

/* ---- wire types (snake_case, straight off the DRF serializers) ---------- */

export type SkillTag = {
  id: number;
  code: string;
  family: string;
  name: string;
  definition: string;
  is_diagnosable: boolean;
  order: number;
};

export type SkillComponentMap = {
  id: number;
  component: string;
  pillar: number;
  tag: number;
  tag_code: string;
  weight: number;
  weak_below: number;
  strong_above: number;
  note: string;
};

export type SkillNodeMap = {
  id: number;
  node_internal_code: string;
  tag: number;
  tag_code: string;
  role: 'primary' | 'secondary';
  indication: string;
  themes: string[];
};

export type BlueprintProfile = {
  id: number;
  slug: string;
  scenario_code: string;
  themes: Record<string, number>;
  instruments: string[];
  periods: number;
  news_count: number;
  difficulty: string;
  max_abs_pcs: number;
  summary: string;
  summary_source: string;
  overridden: boolean;
  source_updated_at: string | null;
  updated: string;
};

export type LLMProvider = {
  id: number;
  name: string;
  kind: 'lmstudio' | 'vllm' | 'openrouter' | 'openai' | 'ollama';
  base_url: string;
  has_api_key: boolean;
  api_key?: string;
  model: string;
  is_active: boolean;
  enabled: boolean;
  priority: number;
  context_window: number | null;
  max_output_tokens: number | null;
  supports_json_schema: boolean | null;
  supports_images: boolean | null;
  sampling_profile: string;
  thinking_enabled: boolean;
  discovered_reasoning_efforts: string[];
  timeout_seconds: number | null;
  extra: Record<string, unknown>;
};

export type SkillMapProblems = { plan: string; problems: string[] };

/* ---- transport ---------------------------------------------------------- */

type ApiMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

const apiErrorMessage = (result: unknown, fallback: string): string => {
  if (typeof result === 'string' && result) return result;
  if (result && typeof result === 'object') {
    const messages = Object.values(result as Record<string, unknown>).flatMap(
      value => (Array.isArray(value) ? value : [value]),
    );
    if (messages.length) return messages.map(String).join(' ');
  }
  return fallback;
};

const request = async <Payload, Result>(
  path: string,
  method: ApiMethod,
  data: Payload,
  fallback: string,
): Promise<Result> => {
  const response = await apiCaller<Payload>({
    path,
    method,
    data,
    apiUrl: API_URL,
  });
  if (response.status.toString()[0] !== '2') {
    throw new Error(apiErrorMessage(response.result, fallback));
  }
  return response.result as Result;
};

/* DRF ViewSets paginate or not depending on settings; accept both shapes. */
const listOf = <T,>(result: unknown): T[] =>
  Array.isArray(result)
    ? (result as T[])
    : (((result as { results?: T[] })?.results ?? []) as T[]);

const getList = async <T,>(path: string, fallback: string): Promise<T[]> =>
  listOf<T>(await request<Record<string, never>, unknown>(path, 'GET', {}, fallback));

/* ---- the four curated tables + providers -------------------------------- */

export const fetchSkillTags = () =>
  getList<SkillTag>('/devplan/admin/skill-tags/', 'Skill tags could not be loaded.');

export const updateSkillTag = (id: number, patch: Partial<SkillTag>) =>
  request<Partial<SkillTag>, SkillTag>(
    `/devplan/admin/skill-tags/${id}/`,
    'PATCH',
    patch,
    'The skill tag could not be saved.',
  );

export const fetchSkillComponents = () =>
  getList<SkillComponentMap>(
    '/devplan/admin/skill-components/',
    'Component mappings could not be loaded.',
  );

export const updateSkillComponent = (id: number, patch: Partial<SkillComponentMap>) =>
  request<Partial<SkillComponentMap>, SkillComponentMap>(
    `/devplan/admin/skill-components/${id}/`,
    'PATCH',
    patch,
    'The component mapping could not be saved.',
  );

export const fetchSkillNodes = () =>
  getList<SkillNodeMap>(
    '/devplan/admin/skill-nodes/',
    'Node mappings could not be loaded.',
  );

export const updateSkillNode = (id: number, patch: Partial<SkillNodeMap>) =>
  request<Partial<SkillNodeMap>, SkillNodeMap>(
    `/devplan/admin/skill-nodes/${id}/`,
    'PATCH',
    patch,
    'The node mapping could not be saved.',
  );

export const fetchSkillMapProblems = (planCode?: string) =>
  request<Record<string, never>, SkillMapProblems>(
    `/devplan/admin/skill-nodes/problems/${planCode ? `?plan=${planCode}` : ''}`,
    'GET',
    {},
    'The skill-map check could not be run.',
  );

export const fetchBlueprintProfiles = () =>
  getList<BlueprintProfile>(
    '/devplan/admin/blueprint-profiles/',
    'Blueprint profiles could not be loaded.',
  );

export const updateBlueprintProfile = (slug: string, summary: string) =>
  request<{ summary: string }, BlueprintProfile>(
    `/devplan/admin/blueprint-profiles/${encodeURIComponent(slug)}/`,
    'PATCH',
    { summary },
    'The blueprint summary could not be saved.',
  );

export const rebuildBlueprintProfile = (slug: string) =>
  request<Record<string, never>, BlueprintProfile>(
    `/devplan/admin/blueprint-profiles/${encodeURIComponent(slug)}/rebuild/`,
    'POST',
    {},
    'The blueprint profile could not be rebuilt.',
  );

export const fetchLLMProviders = () =>
  getList<LLMProvider>('/llm/providers/', 'LLM providers could not be loaded.');

export const activateLLMProvider = (id: number) =>
  request<Record<string, never>, LLMProvider>(
    `/llm/providers/${id}/activate/`,
    'POST',
    {},
    'The provider could not be activated.',
  );

export const updateLLMProvider = (id: number, patch: Partial<LLMProvider>) =>
  request<Partial<LLMProvider>, LLMProvider>(
    `/llm/providers/${id}/`,
    'PATCH',
    patch,
    'The provider could not be saved.',
  );
